import {
  useCallback,
  useState,
  type KeyboardEvent,
  type KeyboardEventHandler,
  type PointerEventHandler,
  type ReactNode,
  type Ref,
} from 'react';
import { Badge, type BadgeType } from '../../atoms/Badge/Badge';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { Text } from '../../atoms/Text/Text';
import { HelpIcon, type HelpIconPlacement } from '../HelpIcon/HelpIcon';
import { Tabs, type TabItem } from '../Tabs/Tabs';
import styles from './CardHeader.module.css';

/**
 * Figma: `Card Header` (518:14321) — 578x28, no annotation. The set moved from
 * 415:47055 in the 2026-09 update and was reworked twice since.
 *
 * The 2026-09-16 update **reverted the type**. A previous 2026-09 pass had
 * promoted the title from `Text sm/leading-none/Medium` (12/12) to `Text
 * md/Medium` (16/16) and grown the help trigger from 12x12 to 16x16; both are
 * back where they started. `Text Wrapper` is 16 tall again (12 of text over
 * `space/4` of underline room) and `Content` is a fixed 20 — sized by the
 * actions row now, not by the title. The row has gone 32 → 28 → 20 across three
 * syncs; the colour never moved, and the title is still `text/secondary`.
 *
 * The title row is now a help affordance rather than a plain label. `Text
 * Wrapper` carries a dashed 1px underline, the help trigger leads the row
 * instead of trailing it, its glyph is `globe` rather than the `Help`
 * component's default `info`, and the title itself dropped from `text/primary`
 * to `text/secondary`. Together they read as "this heading has an explanation",
 * which is what the new `State=Hover` variant then shows: the tooltip open.
 *
 * Structurally still the same content row as `TableHeader` (336:23513) and still
 * with none of its chrome — no fill, stroke, radius or padding, because this one
 * sits inside a `Card` that already draws all of that.
 *
 * **`supportingText` is gone.** Figma deleted the second line; the
 * `Supporting text#518:0` boolean survives in the property panel but references
 * no layer, so nothing can turn it on. Removed here rather than kept as a
 * code-only extra — see plan.md §7.
 *
 * `Actions#518:1` drives `Right Wrapper`'s visibility, and the 2026-09-14
 * update gave it three named children with a boolean each — `Tab#842:128`,
 * `AI summary#842:131` and `More#842:134`. `Actions Wrapper` used to be an
 * empty frame; it now holds, `space/12` apart:
 *
 *   Tabs        `Size=sm, Type=Button border, Full width=False`, 84x20, drawn
 *               as a two-tab `Bar` / `Line` chart-type switcher with `Bar`
 *               current. Eight further tabs sit hidden in the instance —
 *               leftovers of the `Tabs` component's own ten, not content.
 *   AI summary  `Buttons/Button`, `Hierarchy=Ghost Color`, leading `ai spark`,
 *               label "AI summary", 91x20.
 *   More        `Buttons/Button`, `Hierarchy=Ghost Grey, Icon only=True`, the
 *               `more` ellipsis, 20x20.
 *
 * 84 + 12 + 91 + 12 + 20 = the 219 `Right Wrapper` reports.
 *
 * Each becomes an optional prop that renders nothing when absent, which is what
 * a boolean toggle means here — the repo's convention for a Figma boolean.
 * They render the components Figma names rather than taking arbitrary nodes,
 * because which button this is *is* the design decision. `actions` stays as the
 * escape hatch and renders after the three.
 *
 * Neither `Supporting text#518:0` nor the hidden `Badge` instance references a
 * layer or a property, so both remain inert — unchanged from the last sync.
 *
 * **`Type` — `Default | Draggable`, new on 2026-09-18** with the Custom Reports
 * page (990:44858). `Type=Draggable` (996:30988) leaves the title row exactly
 * as it is — same underline, same leading `globe`, same `text/secondary` — and
 * swaps the whole actions row for two Ghost Grey icon-only buttons `space/12`
 * apart: a `ri:draggable` handle, then a `close`. Identical geometry to the
 * three controls it replaces, so the 20-tall row does not move.
 *
 * It does not become a `type` prop. Every other Figma boolean in this row is
 * already "provide a handler to render it", and a `type` would be a second,
 * redundant way to say the same thing — one that can disagree with the content,
 * which is the bug `Card`'s own `chart2` note argues against. So `dragHandle`
 * and `onRemove` render the two controls, and passing neither *is*
 * `Type=Default`.
 *
 * No frame combines the two sets, so their order together is a decision rather
 * than a reading: the draggable pair render after the default trio and before
 * the `actions` escape hatch, which keeps `close` rightmost the way the file
 * draws it.
 */
/** The `Tabs` instance in the actions row, as much of it as a caller drives. */
export type CardHeaderTabs = {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  /**
   * Accessible name for the switcher. Not in Figma. Defaults to "Chart type",
   * which is what the `Bar` / `Line` pair the file draws actually selects.
   */
  label?: string;
};

/**
 * Figma `Type=Draggable`'s handle, as much of it as a caller drives.
 *
 * A config object rather than a `ReactNode` slot, for the reason `tabs` is one:
 * *which button this is* is the design's decision, not the caller's. What the
 * caller owns is the gesture — so this carries the listeners and the ref a
 * reorder needs and nothing about how the handle looks.
 *
 * None of it is in Figma, which defines appearance and not behaviour.
 */
export type CardHeaderDragHandle = {
  /** Start the drag on the press. `onClick` fires after the release, too late. */
  onPointerDown?: PointerEventHandler<HTMLElement>;
  /** Keyboard reordering — pick up, move, drop. See `ReportGrid`. */
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
  /** So the grid can return focus to the handle after a keyboard drop. */
  ref?: Ref<HTMLButtonElement>;
  /** Accessible name. An icon-only button has no text to name it. */
  label?: string;
  /** Element holding the "press Space to pick up" instructions. */
  describedBy?: string;
  /** True while this widget is picked up, reported as `aria-pressed`. */
  pressed?: boolean;
};

export type CardHeaderProps = {
  /** Figma `Text` — the title. */
  title: string;
  /**
   * Figma `Badge#2026:0` — a `Type=Gray` `Badge` after the title, inside the
   * underlined `Text Wrapper` at its `space/6` gap. Omit for none.
   *
   * Live as of 2026-09-18. The layer was there before, hidden and referenced by
   * nothing (see the note above); the Competitors frame (2030:19055) is the
   * first to wire it up, through its two `Table Header`s, to count what the
   * table holds — "50 total pages". The badge *type* defaults to the design's
   * `Gray` — see `badgeType` for the one instance that overrides it.
   *
   * It sits under the dashed underline because Figma puts it inside the
   * wrapper that carries it, so the count reads as part of the heading. The
   * title truncates before the badge does: a clipped count is a wrong number,
   * a clipped title is still the title.
   */
  badge?: string;
  /**
   * Overrides the badge's `Type`. Defaults to `gray`, which is what the
   * component draws and what every table header in the app used until
   * 2026-09-21.
   *
   * Settings' Usage frame (2135:63277) is the first instance to override it —
   * `AI usage breakdown`'s "5 logs" is `Type=Info` where the Sentiment tuning
   * and Team members headers on the same page stay gray. Put to the user as
   * possible drift, and the answer was to draw it as designed, so the override
   * is a prop here rather than a fork of the header.
   */
  badgeType?: BadgeType;
  /**
   * An `id` for the title element, so something else can be *named* by it — a
   * dialog whose heading is this title (`aria-labelledby`), as the Tune
   * sentiment modal's is. Not in Figma, which has no accessible names.
   */
  titleId?: string;
  /**
   * Bumped each time a dragged widget's magnet catches a slot, which flashes
   * the title once. Not in Figma, which draws no drag with snapping; see
   * `views/reports/magnet.ts` for why the feedback is on the dragged card's own
   * title rather than on the slot it caught — the slot already wears the ring,
   * and the eye is on the card in hand.
   *
   * A number rather than a flag because the *change* is the event: catching a
   * second slot has to read as a second flash, not as a state that was already
   * true. The `<Text>` is keyed on it so the animation restarts from the top.
   */
  flash?: number;
  /**
   * The explanation, which renders as the tooltip's *supporting* line — the
   * bubble's first line is `title`. Omit for no help icon, which also removes
   * the underline, since without an explanation there is nothing for the
   * affordance to promise.
   */
  helpText?: string;
  /** A second sentence, appended to `helpText` in the same supporting line. */
  helpSupportingText?: string;
  /**
   * Figma `Icon` (instance swap) on the nested `Help`. Defaults to `globe`,
   * which is now the `Help` component's own default too — it used to be `info`
   * and this header overrode it; as of 2026-09 the override and the default
   * agree, so passing nothing gives the same glyph either way.
   */
  helpIcon?: ReactNode;
  /**
   * Figma `Tooltip` on the nested `Help`. Defaults to `topRight`.
   *
   * The two variants disagree: `State=Default` sets `Top no arrow` and
   * `State=Hover` sets `Top right`. Only the Hover variant actually shows a
   * bubble, so `Top right` is the one the file draws — and it is the one that
   * works, because the trigger leads the row and a centred bubble hangs off the
   * card's left edge. Flagged in plan.md §7.
   */
  helpPlacement?: HelpIconPlacement;
  /**
   * Figma `State=Hover` — whether the help tooltip is showing. Omit to let the
   * row own it; hovering or focusing the title opens it.
   */
  helpOpen?: boolean;
  /**
   * Figma `Tab#842:128` — the `Size=sm` chart-type switcher leading the actions
   * row. Omit to leave it out. Figma draws `Bar` / `Line` with `Bar` current.
   */
  tabs?: CardHeaderTabs;
  /**
   * Figma `AI summary#842:131` — the Ghost Colour button with the `ai spark`
   * glyph. Provide a handler to render it.
   */
  onAiSummary?: () => void;
  /** Label for that button. Figma draws "AI summary". */
  aiSummaryLabel?: string;
  /**
   * Figma `More#842:134` — the trailing Ghost Grey icon-only button. Provide a
   * handler to render it.
   */
  onMore?: () => void;
  /**
   * Accessible name for the more button. Not in Figma — an icon-only button has
   * no text to name it.
   */
  moreLabel?: string;
  /**
   * Figma `Type=Draggable`'s `ri:draggable` button. Provide it to render the
   * handle; see `CardHeaderDragHandle`.
   */
  dragHandle?: CardHeaderDragHandle;
  /**
   * Figma `Type=Draggable`'s trailing `close` button. Provide a handler to
   * render it. On a report widget this takes the widget off the report.
   */
  onRemove?: () => void;
  /** Accessible name for that button. Not in Figma. */
  removeLabel?: string;
  /**
   * Figma `Actions Wrapper` — extra trailing content, rendered after the
   * controls above.
   */
  actions?: ReactNode;
  className?: string;
};

export function CardHeader({
  title,
  badge,
  badgeType = 'gray',
  titleId,
  flash = 0,
  helpText,
  helpSupportingText,
  helpIcon,
  helpPlacement = 'topRight',
  helpOpen,
  tabs,
  onAiSummary,
  aiSummaryLabel = 'AI summary',
  onMore,
  moreLabel = 'More options',
  dragHandle,
  onRemove,
  removeLabel = 'Remove',
  actions,
  className,
}: CardHeaderProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = helpOpen ?? uncontrolledOpen;

  const setOpen = useCallback(
    (next: boolean) => {
      if (helpOpen === undefined) setUncontrolledOpen(next);
    },
    [helpOpen],
  );

  const hasHelp = helpText !== undefined;
  /* Figma `Actions#518:1` — the wrapper is drawn only when it has something in it. */
  const hasActions =
    tabs !== undefined ||
    onAiSummary !== undefined ||
    onMore !== undefined ||
    dragHandle !== undefined ||
    onRemove !== undefined ||
    actions !== undefined;

  /*
   * Figma's `State=Hover` shows the tooltip open, and the underline says the
   * whole title is the affordance — so the row opens it, not just the 12px
   * glyph. `HelpIcon`'s own hover and focus handlers still fire but are inert
   * while `open` is controlled and no `onOpenChange` is passed, which is
   * deliberate: letting them through would close the tooltip the moment the
   * pointer crossed off the icon and onto the title beside it, without ever
   * leaving the row.
   *
   * That leaves Escape to re-implement here, since it lived in the handlers now
   * being bypassed. `onFocus`/`onBlur` are React's bubbling versions, so
   * focusing the trigger inside opens the tooltip for keyboard users too.
   */
  const rowHandlers = hasHelp
    ? {
        onPointerEnter: () => setOpen(true),
        onPointerLeave: () => setOpen(false),
        onFocus: () => setOpen(true),
        onBlur: () => setOpen(false),
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Escape') setOpen(false);
        },
      }
    : {};

  return (
    <div className={[styles.content, className].filter(Boolean).join(' ')}>
      <div
        className={[styles.textWrapper, hasHelp ? styles.underlined : null]
          .filter(Boolean)
          .join(' ')}
        {...rowHandlers}
      >
        {/* Figma puts the help trigger first — it led the title as of 2026-09. */}
        {hasHelp && (
          <HelpIcon
            /*
             * The bubble leads with the card's own title and drops the
             * explanation to the supporting line, so the tooltip names what it
             * is describing before describing it. Figma's `Help` carries both
             * slots but binds no content to either — the file's `Card Header`
             * ships the instance with `Supporting text=False` and the
             * placeholder string — so the pairing is set here.
             *
             * `helpSupportingText` joins the same paragraph rather than taking
             * a third line: the Figma tooltip has exactly two.
             */
            text={title}
            supportingText={[helpText, helpSupportingText].filter(Boolean).join(' ')}
            icon={helpIcon ?? <Icon name="globe" />}
            placement={helpPlacement}
            open={isOpen}
          />
        )}

        {/* Figma: `Text sm/leading-none/Medium` — 12/12 — in text/secondary. */}
        <Text
          key={flash}
          id={titleId}
          size="sm"
          leading="none"
          weight="medium"
          color="secondary"
          truncate
          className={flash > 0 ? styles.flash : undefined}
        >
          {title}
        </Text>

        {/* Figma `Badge#2026:0` — `Type=Gray`, after the title. */}
        {badge !== undefined && (
          <Badge type={badgeType} className={styles.badge}>
            {badge}
          </Badge>
        )}
      </div>

      {hasActions && (
        <div className={styles.actions}>
          {/* Figma `Tab` — `Size=sm`, hugging, leading the row. */}
          {tabs && (
            <Tabs
              size="sm"
              label={tabs.label ?? 'Chart type'}
              items={tabs.items}
              value={tabs.value}
              defaultValue={tabs.defaultValue}
              onChange={tabs.onChange}
            />
          )}

          {/* Figma `AI summary` — Ghost Colour, leading `ai spark`. */}
          {onAiSummary && (
            <Button
              hierarchy="ghostColor"
              iconLeading={<Icon name="aiSpark" />}
              onClick={onAiSummary}
            >
              {aiSummaryLabel}
            </Button>
          )}

          {/* Figma `More` — Ghost Grey, icon only. */}
          {onMore && (
            <Button
              hierarchy="ghostGrey"
              iconLeading={<Icon name="more" />}
              onClick={onMore}
              aria-label={moreLabel}
            />
          )}

          {/*
            Figma `Type=Draggable` (996:30988) — `ri:draggable` then `close`,
            both Ghost Grey icon-only, `space/12` apart like every other pair in
            this row.

            `aria-pressed` is not in Figma and is what makes the handle honest
            to assistive tech while a widget is picked up: the button holds a
            state, and colour alone cannot report it.
          */}
          {dragHandle && (
            <Button
              hierarchy="ghostGrey"
              iconLeading={<Icon name="draggable" />}
              aria-label={dragHandle.label ?? `Reorder ${title}`}
              aria-describedby={dragHandle.describedBy}
              aria-pressed={dragHandle.pressed}
              onPointerDown={dragHandle.onPointerDown}
              onKeyDown={dragHandle.onKeyDown}
              ref={dragHandle.ref}
            />
          )}

          {onRemove && (
            <Button
              hierarchy="ghostGrey"
              iconLeading={<Icon name="close" />}
              onClick={onRemove}
              aria-label={`${removeLabel} ${title}`}
            />
          )}

          {actions}
        </div>
      )}
    </div>
  );
}
